<script setup>
import { onMounted, onBeforeUnmount, watch, ref, watchEffect } from "vue";
import { useRouter } from "vue-router";
import { useChart } from "@/composables/useChart.js";
import { useApp } from "@/composables/useApp.js";
import { useAppBarActions } from "@/composables/useAppBarActions.js";
import SalesFilter from "@/components/SalesFilter.vue";
import BaseSalesChart from "./BaseSalesChart.vue";

const router = useRouter();
const { isDev } = useApp();
const { setActions, clearActions } = useAppBarActions();

// Use the combined chart composable (includes filter functionality)
const {
  // Chart functions
  getChartOptions,
  formatCurrency,
  formatAmount,

  // Chart state
  chartData,
  chartLoading,
  chartError,
  chartType,
  isDailyChart,
  showChartMenu,
  chartExporting,

  // Chart computed properties
  hasData,
  filteredData,
  totalSales,
  averagePerTimeUnit,
  bestTimeUnit,
  growthRate,
  totalsSummary,

  // Chart actions
  fetchSalesChartData,

  // Filter state
  isFilterExpanded,
  hasActiveFilters,
  numberOfActiveFilters,
  activeFilters,

  // Filter actions
  toggleFilterExpand,
  shallowEqual,
} = useChart();

// Add ref for BaseSalesChart
const baseChartRef = ref(null);

const forceChartUpdate = () => {
  // console.log("Force chart update called from parent");
  if (baseChartRef.value && baseChartRef.value.forceChartUpdate) {
    baseChartRef.value.forceChartUpdate();
  }
};

const handleExportChart = async () => {
  if (!baseChartRef.value || !hasData.value) return;

  chartExporting.value = true;
  try {
    // If BaseSalesChart has export functionality
    if (baseChartRef.value.exportChart) {
      await baseChartRef.value.exportChart();
    } else {
      // Implement fallback export
      const canvas = document.querySelector("canvas");
      if (canvas) {
        const link = document.createElement("a");
        link.download = `daily-${new Date().toISOString().split("T")[0]}.png`;
        link.href = canvas.toDataURL("image/png");
        link.click();
      }
    }
  } catch (error) {
    console.error("Export failed:", error);
  } finally {
    chartExporting.value = false;
  }
};

// App bar actions
watchEffect(() => {
  setActions([
    {
      icon: "mdi-arrow-left",
      tooltip: "Back to Daily Sales",
      onClick: () => router.push("/admin/sales/daily"),
    },
    {
      icon: "mdi-filter-variant",
      tooltip: "Filters",
      onClick: toggleFilterExpand,
      badge: numberOfActiveFilters.value > 0 ? { content: numberOfActiveFilters.value, show: true, color: "error" } : null,
    },
    {
      icon: "mdi-download",
      tooltip: "Export chart",
      onClick: handleExportChart,
      loading: chartExporting.value,
    },
  ]);
});
onBeforeUnmount(() => clearActions());

// Watchers

// Watch for filter changes and refresh chart
watch(activeFilters, async (val, oldVal) => {
  // console.log("Monthly Averages: Active filters changed from", oldVal, "to", val);
  if (!shallowEqual(val, oldVal)) {
    // console.log("Filters changed, fetching new averages data");
    isDailyChart.value = true;
    await fetchSalesChartData(null, true);
  }
});

// Watch filteredData and force chart update when it changes
watch(
  filteredData,
  (newData, oldData) => {
    // Force chart update when filteredData changes
    setTimeout(() => {
      forceChartUpdate();
    }, 100);
  },
  { deep: true },
);

// Watch chartType and force update
watch(chartType, (newType) => {
  setTimeout(() => {
    forceChartUpdate();
  }, 50);
});

// Initialize on mount
onMounted(async () => {
  // Set chart type
  chartType.value = "bar";
  // Set to daily mode
  isDailyChart.value = true;
  await fetchSalesChartData(null, true);
});
</script>

<template>
  <v-container fluid class="pa-0">
    <!-- Sales Filter Component -->
    <SalesFilter />

    <!-- Main Chart Area -->
    <v-row>
      <v-col cols="12" class="mt-0">
        <!-- ascolta l'evento 'export-chart' e quando lo riceve esegue la funzione 'handleExportChart' -->
        <BaseSalesChart
          ref="baseChartRef"
          :is-daily="true"
          :is-dev="isDev"
          :chart-type="chartType"
          :filtered-data="filteredData"
          :has-active-filters="hasActiveFilters"
          :has-data="hasData"
          :loading="chartLoading"
          :error="chartError"
          :chart-data="chartData"
          :totals-summary="totalsSummary"
          :total-sales="totalSales"
          :average-per-time-unit="averagePerTimeUnit"
          :best-time-unit="bestTimeUnit"
          :growth-rate="growthRate"
          :get-chart-options="getChartOptions"
          :format-currency="formatCurrency"
          :format-amount="formatAmount"
          @export-chart="handleExportChart"
        />
      </v-col>
    </v-row>
  </v-container>
</template>

